import React, { useEffect, useRef, useState } from "react";

import { DragDropContext, DropResult } from "@hello-pangea/dnd";

import {
  getLocalStorageCropExplorerPseudoUserId,
  useAddCropExplorationEntry,
  useDeleteCropExplorationEntriesByYear,
  useDeleteCropExplorationEntry,
  useDuplicateCropExplorationEntry,
  useTranslation,
  useUpdateCropExploration,
  useUpdateCropExplorationTemplate,
} from "utils";

import { CROP_TYPE, CropExplorationResponse, CropExplorationTemplateResponse, EntriesByCrops, YEAR_TYPE } from "models";

import { Flex, Icon, Message as message } from "components";

import { useCropExplorationContext } from "pages/app/crop-explorer/CropExplorationContext";
import { CropExplorerTimelineColumn } from "pages/app/crop-explorer/components/CropExplorerTimelineColumn";
import { CropExplorerTimelineYearHeader } from "pages/app/crop-explorer/components/CropExplorerTimelineYearHeader";

export const CropExplorerTimeline = ({
  cropExploration: cropExplorationFromProps,
  isAdmin,
}: {
  cropExploration: CropExplorationResponse | CropExplorationTemplateResponse;
  isAdmin?: boolean;
}) => {
  const pseudoUserId = getLocalStorageCropExplorerPseudoUserId();
  const { t } = useTranslation("cropExplorer");
  const { tourRefStep2BauSection, tourRefStep3YearSection } = useCropExplorationContext();

  const { mutateAsync: addCropExplorationEntryAndSkipCacheInvalidation } = useAddCropExplorationEntry(!!isAdmin, true);
  const { mutateAsync: deleteCropExplorationEntry } = useDeleteCropExplorationEntry(!!isAdmin);

  const { mutate: updateCropExploration, isError: isUpdateCropExplorationError } = useUpdateCropExploration();
  const { mutate: updateCropExplorationTemplate, isError: isUpdateCropExplorationTemplateError } = useUpdateCropExplorationTemplate();
  const { mutate: deleteCropExplorationEntriesByYear } = useDeleteCropExplorationEntriesByYear(!!isAdmin);
  const { mutate: duplicateCropExplorationEntry } = useDuplicateCropExplorationEntry(!!isAdmin);

  // we may need to optimistically update the UI, so we hold a copy of the crop exploration in state
  const [cropExploration, setCropExploration] = useState<CropExplorationResponse | CropExplorationTemplateResponse>(cropExplorationFromProps);
  const [yearBeingAdded, setYearBeingAdded] = useState<number | null>(null);
  const timelineContainerRef = useRef<HTMLDivElement | null>(null);

  const handleYearAdded = (isAdmin: boolean) => {
    if (cropExploration.yearlyEntryYears.length > 4) {
      return;
    }

    const newYearlyDataYears = cropExploration.yearlyEntryYears.length
      ? [...cropExploration.yearlyEntryYears, cropExploration.yearlyEntryYears.slice(-1)[0] + 1]
      : [cropExploration.bauYears.slice(-1)[0] + 1];

    setYearBeingAdded(newYearlyDataYears[newYearlyDataYears.length - 1]);

    // TODO: maybe add new endpoint called addYear
    if (isAdmin) {
      updateCropExplorationTemplate({
        id: cropExploration.id,
        cropExploration: { ...cropExploration, yearlyEntryYears: newYearlyDataYears } as CropExplorationTemplateResponse,
      });
      return;
    }

    updateCropExploration({
      id: cropExploration.id,
      cropExploration: { ...cropExploration, yearlyEntryYears: newYearlyDataYears, pseudoUserId } as CropExplorationResponse,
    });
  };

  const handleYearDeleted = (_year: number, deleteYear: boolean) => {
    deleteCropExplorationEntriesByYear({ id: cropExploration.id, year: _year.toString(), deleteYear, pseudoUserId });
  };

  const handleYearDuplicated = (sourceYear: number, targetYear: number) => {
    duplicateCropExplorationEntry({ id: cropExploration.id, sourceYear, targetYear, pseudoUserId });
  };

  const checkYearEntries = (entries: EntriesByCrops[], year: number): boolean => {
    const yearEntries = entries.find((entry) => entry.year === year);
    if (!yearEntries) return false;

    return !!yearEntries.main || yearEntries.side.length > 0;
  };

  const scrollToTimelineSection = (type: YEAR_TYPE) => {
    if (type === YEAR_TYPE.BASELINE) tourRefStep2BauSection.current?.scrollIntoView({ behavior: "smooth", inline: "end" });
    if (type === YEAR_TYPE.YEARLY) tourRefStep3YearSection.current?.scrollIntoView({ behavior: "smooth", inline: "start" });
  };

  // Drag and drop logic
  const handleDragEnd = async (e: DropResult) => {
    if (!e.destination) {
      return;
    }

    const [sourceId, sourceCropId] = e.draggableId.split("_");
    const [sourceYear, sourceType] = e.source.droppableId.split("_");
    const [destinationYear, destinationType] = e.destination.droppableId.split("_");

    const sourceIsBau = cropExploration.bauYears.includes(parseInt(sourceYear));
    const destinationIsBau = cropExploration.bauYears.includes(parseInt(destinationYear));

    const sourceBauOrYearlyEntries = sourceIsBau ? cropExploration.bauEntries : cropExploration.yearlyEntries;
    const destinationBauOrYearlyEntries = destinationIsBau ? cropExploration.bauEntries : cropExploration.yearlyEntries;

    const sourceYearEntries = sourceBauOrYearlyEntries.find((item) => item.year === parseInt(sourceYear));
    const destinationYearEntries = destinationBauOrYearlyEntries.find((item) => item.year === parseInt(destinationYear));

    const destinationYearHasMainCrop = !!destinationYearEntries?.main;

    if (e.source.droppableId === e.destination?.droppableId) {
      // if the crop is dragged to the same year / swimlane it came from, do nothing
      return;
    }

    if (destinationType === CROP_TYPE.MAIN && destinationYearHasMainCrop) {
      // if the destination is the main crop swimlane, and there's already a crop there, do nothing
      message({ content: t("timeline.errors.duplicateMainCrop"), icon: "error" });
      return;
    }

    if (destinationType === CROP_TYPE.SIDE && destinationYearEntries?.side.find((sideCrop) => sideCrop.cropId === sourceCropId)) {
      // if the destination is the side crop swimlane, and there's already an instance of this crop there, do nothing
      message({ content: t("timeline.errors.duplicateSideCrop"), icon: "error" });
      return;
    }

    const sourceCrop = sourceType === CROP_TYPE.MAIN ? sourceYearEntries?.main : sourceYearEntries?.side.find((entry) => entry.id === sourceId);

    const copyOfSourceBauOrYearlyEntries = [...sourceBauOrYearlyEntries];
    const copyOfDestinationBauOrYearlyEntries = [...destinationBauOrYearlyEntries];

    const entryToDeleteFrom = copyOfSourceBauOrYearlyEntries.find((entry) => entry.year === parseInt(sourceYear));
    const entryToAddTo = copyOfDestinationBauOrYearlyEntries.find((entry) => entry.year === parseInt(destinationYear));

    if (!entryToDeleteFrom || !entryToAddTo) {
      return;
    }

    if (sourceType === CROP_TYPE.MAIN) {
      if (destinationType === CROP_TYPE.MAIN) {
        entryToAddTo.main = entryToDeleteFrom.main;
      } else {
        entryToAddTo.side.push(entryToDeleteFrom.main!);
      }
      entryToDeleteFrom.main = null;
    }

    if (sourceType === CROP_TYPE.SIDE) {
      if (destinationType === CROP_TYPE.MAIN) {
        entryToAddTo.main = sourceCrop!;
      } else {
        entryToAddTo.side.push(sourceCrop!);
      }
      entryToDeleteFrom.side = entryToDeleteFrom.side.filter((crop) => crop.id !== sourceId);
    }

    const optimisticallyUpdatedCropExploration = {
      ...cropExploration,
      [sourceIsBau ? "bauEntries" : "yearlyEntries"]: copyOfSourceBauOrYearlyEntries,
      [destinationIsBau ? "bauEntries" : "yearlyEntries"]: copyOfDestinationBauOrYearlyEntries,
    };

    setCropExploration(optimisticallyUpdatedCropExploration);

    await addCropExplorationEntryAndSkipCacheInvalidation({
      cropExplorationId: cropExploration.id,
      crop: {
        cropId: sourceCropId,
        year: parseInt(destinationYear),
        residue: sourceCrop?.residue || false,
        type: destinationType === CROP_TYPE.MAIN ? CROP_TYPE.MAIN : CROP_TYPE.SIDE,
      },
      pseudoUserId,
    });

    await deleteCropExplorationEntry({ id: sourceId, pseudoUserId });
  };

  useEffect(() => {
    // this is to reset any optimistic UI updates
    setCropExploration(cropExplorationFromProps);
  }, [cropExplorationFromProps]);

  useEffect(() => {
    if (!yearBeingAdded) return;
    if (isUpdateCropExplorationError || isUpdateCropExplorationTemplateError) {
      setYearBeingAdded(null);
      return;
    }

    if (cropExploration.yearlyEntryYears.includes(yearBeingAdded)) {
      setYearBeingAdded(null);
      timelineContainerRef.current?.scrollIntoView({ behavior: "smooth", inline: "end" });
    }
  }, [cropExploration, isUpdateCropExplorationError, isUpdateCropExplorationTemplateError, timelineContainerRef, yearBeingAdded]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div data-testid="crop-explorer-main-timeline" className="timeline padding-100" ref={timelineContainerRef}>
        <div>
          <div className="section-navigation" onClick={() => scrollToTimelineSection(YEAR_TYPE.BASELINE)}>
            <span ref={tourRefStep2BauSection} className="paragraph-m-bold grey-600-text margin-bottom-100">
              {t("timeline.headers.baseline")}
            </span>
          </div>
          <div className="section-content">
            {cropExploration.bauEntries
              .sort((a, b) => a.year - b.year)
              .map((entry, i) => (
                <Flex data-testid={`crop-explorer-main-timeline-base-year-${entry.year}`} key={entry.year} vertical={true} gap="1rem">
                  <CropExplorerTimelineYearHeader
                    year={entry.year}
                    t={t}
                    type={YEAR_TYPE.BASELINE}
                    baselineYears={cropExploration.bauYears}
                    hasEntries={checkYearEntries(cropExploration.bauEntries, entry.year)}
                    previousYearHasEntries={checkYearEntries(cropExploration.bauEntries, entry.year - 1)}
                    nextYearHasEntries={checkYearEntries(
                      i === cropExploration.bauEntries.length - 1 ? cropExploration.yearlyEntries : cropExploration.bauEntries,
                      entry.year + 1
                    )}
                    allowDuplicateToPreviousYear={i > 0}
                    allowDuplicateToNextYear
                    onYearAdded={() => handleYearAdded(!!isAdmin)}
                    onYearDuplicated={handleYearDuplicated}
                    onYearDeleted={handleYearDeleted}
                  />
                  <CropExplorerTimelineColumn entry={entry} cropExploration={cropExploration} isAdmin={isAdmin} columnIndex={i} />
                </Flex>
              ))}
          </div>
        </div>
        <div>
          <div className="section-navigation" onClick={() => scrollToTimelineSection(YEAR_TYPE.YEARLY)}>
            <span ref={tourRefStep3YearSection} className="paragraph-m-bold grey-600-text margin-bottom-100">
              {t("timeline.headers.yearly")}
            </span>
          </div>
          <div className="section-content">
            {cropExploration.yearlyEntries.map((entry, i) => (
              <Flex key={entry.year} vertical={true} gap="1rem">
                <CropExplorerTimelineYearHeader
                  year={entry.year}
                  t={t}
                  type={YEAR_TYPE.YEARLY}
                  yearlyYears={cropExploration.yearlyEntryYears}
                  hasEntries={checkYearEntries(cropExploration.yearlyEntries, entry.year)}
                  previousYearHasEntries={checkYearEntries(i === 0 ? cropExploration.bauEntries : cropExploration.yearlyEntries, entry.year - 1)}
                  nextYearHasEntries={checkYearEntries(cropExploration.yearlyEntries, entry.year + 1)}
                  allowDuplicateToPreviousYear
                  allowDuplicateToNextYear={i < 4}
                  onYearAdded={() => handleYearAdded(!!isAdmin)}
                  onYearDuplicated={handleYearDuplicated}
                  onYearDeleted={handleYearDeleted}
                />
                <CropExplorerTimelineColumn entry={entry} cropExploration={cropExploration} isAdmin={isAdmin} isTourStep={i === 0} columnIndex={i} />
              </Flex>
            ))}

            {cropExploration.yearlyEntryYears.length < 5 && (
              <button
                data-testid="crop-explorer-timeline-add-year-button"
                className="add-year-cta-button"
                onClick={(e) => {
                  handleYearAdded(!!isAdmin);
                  // remove focus from the button after the function runs to remove the css :focus styling
                  if (e.target instanceof HTMLButtonElement) e.target.blur();
                }}
              >
                <Icon iconName="klim/plus" size="s" />
              </button>
            )}
          </div>
        </div>
      </div>
    </DragDropContext>
  );
};
